import { useRef } from 'react';
import { cx } from '@emotion/css';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Grade, GradeChangeInput } from '@/pages/AccountInformation/AccountInformation.types';
import { useAriaInvalid } from '@/hooks';
import styles from './SelectInput.styles';
import colors from '@/theme/colors';

interface SelectInputProps {
  label: string;
  childUid: string;
  options: Grade[];
  value: Grade;
  onChange: (input: GradeChangeInput, childUId: string) => void;
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
}

export const SelectInput: React.FC<SelectInputProps> = ({
  label,
  childUid,
  options,
  value,
  onChange,
  error = false,
  errorMessage,
  required = false,
}) => {
  const handleChange = (event: SelectChangeEvent) => {
    const grade = options.find(option => option.id === event.target.value);
    if (grade) {
      onChange(grade, childUid);
    }
  };

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const labelId = `gradeLabelId${childUid}`;
  const listboxId = `gradeListbox${childUid}`;
  const descriptionId = `gradeInput${childUid}-description`;

  useAriaInvalid(
    () => wrapperRef.current?.querySelector<HTMLDivElement>('.MuiSelect-select'),
    error,
    [childUid]
  );

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <FormControl
        variant="standard"
        fullWidth
        className={cx(
          styles.container,
          required && styles.required,
          error && styles.containerError
        )}
        required={required}
      >
        <InputLabel
          htmlFor={`gradeInput${childUid}`}
          id={labelId}
          className={styles.label}
          required={required}
        >
          {label}
        </InputLabel>
        <Select
          labelId={labelId}
          value={value?.id.toString() || ''}
          onChange={handleChange}
          label={label}
          inputProps={{
            id: `gradeInput${childUid}`,
            name: `grade-${childUid}`,
            'data-testid': 'gradeInput',
            'aria-required': required,
            ...(error && errorMessage && { 'aria-describedby': descriptionId }),
          }}
          SelectDisplayProps={{
            'aria-controls': listboxId,
          }}
          MenuProps={{
            MenuListProps: {
              id: listboxId,
              'aria-labelledby': labelId,
            },
          }}
        >
          {options.map(({ id, description }) => (
            <MenuItem key={id} value={id} data-testid="gradeOption" className={styles.optionItem}>
              {description}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {error && errorMessage && (
        <span id={descriptionId} className={styles.errorMessage} role="alert">
          {errorMessage}
        </span>
      )}
    </div>
  );
};
